/**
 * ProtectedRoute Component
 * 
 * Route guard that requires authentication.
 * Redirects unauthenticated users to Cognito Hosted UI.
 */

import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { cognitoService } from '@/services/cognito';

/**
 * ProtectedRoute component
 * 
 * Wraps routes that require authentication.
 * If user is not authenticated, redirects to Cognito Hosted UI.
 * If user is authenticated, renders child routes via Outlet.
 * 
 * @example
 * ```tsx
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/home" element={<HomePage />} />
 * </Route>
 * ```
 */
export function ProtectedRoute() {
  const { isAuthenticated, isLoading } = useAuthStore();
  const skipAuth = import.meta.env.VITE_SKIP_AUTH === 'true';
  const hasRedirected = useRef(false);

  // Redirect to Cognito Hosted UI if not authenticated
  // This hook must be called unconditionally (before any returns)
  useEffect(() => {
    // Only redirect once to avoid loops
    if (!skipAuth && !isAuthenticated && !isLoading && !hasRedirected.current) {
      console.log('User not authenticated, redirecting to Cognito Hosted UI...');
      hasRedirected.current = true;
      cognitoService.redirectToLogin();
    }
  }, [isAuthenticated, isLoading, skipAuth]);

  // Skip authentication in development mode
  if (skipAuth) {
    return <Outlet />;
  }

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="mt-4 text-sm text-muted-foreground">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  // Show loading while redirecting
  if (!isAuthenticated) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="mt-4 text-sm text-muted-foreground">Redirigiendo a inicio de sesión...</p>
        </div>
      </div>
    );
  }

  // Render child routes
  return <Outlet />;
}
